import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import NotificationBell from '../components/NotificationBell';
import LoginModal from '../components/LoginModal';
import ClientSidebar from '../components/ClientSidebar';
import hygeaLogo from '../../assets/hygea.png';
import ChatBotTrigger from '../../components/ChatBotTrigger';

import { API_BASE } from '../../config/runtime';

const getStoredClientUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.role || '').toLowerCase() === 'client' ? parsed : null;
  } catch {
    return null;
  }
};

// ── Edit Booking Modal ─────────────────────────────────────────────────────
const EditBookingModal = ({ booking, services, clientUser, onClose, onSave, saving }) => {
  const normalizeContact = (value) => (value || '').replace(/\D/g, '').slice(0, 11);
  const normalizeTimeValue = (value) => {
    if (!value) return '';
    return value.length === 5 ? `${value}:00` : value;
  };
  const buildSelectedServices = (targetBooking, serviceCatalog) => {
    if (!targetBooking) return [];
    const catalog = Array.isArray(serviceCatalog) ? serviceCatalog : [];
    if (Array.isArray(targetBooking.services) && targetBooking.services.length > 0) {
      return targetBooking.services.map((svc) => {
        const match = catalog.find((item) => String(item.id) === String(svc.service_id));
        if (match) return match;
        return { id: Number(svc.service_id || svc.id), name: svc.service_name || svc.name || 'Service', duration_minutes: svc.duration_minutes || null, price: Number(svc.price || 0), branch_ids: [] };
      });
    }
    if (targetBooking.service_id) {
      const match = catalog.find((item) => String(item.id) === String(targetBooking.service_id));
      if (match) return [match];
      return [{ id: Number(targetBooking.service_id), name: targetBooking.service_name || 'Service', duration_minutes: targetBooking.duration_minutes || null, price: Number(targetBooking.service_price || 0), branch_ids: [] }];
    }
    return [];
  };

  const [branches, setBranches] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [formData, setFormData] = useState({ full_name: '', contact_number: '', branch_id: '', therapist_id: '', number_of_clients: 1, appointment_date: '', appointment_time: '' });
  const [selectedServices, setSelectedServices] = useState([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!booking) return;
    setFormData({ full_name: booking?.guest_name || clientUser?.full_name || clientUser?.name || '', contact_number: normalizeContact(booking?.guest_contact || clientUser?.contact_number || clientUser?.phone || ''), branch_id: booking?.branch_id ? String(booking.branch_id) : '', therapist_id: booking?.therapist_id ? String(booking.therapist_id) : '', number_of_clients: Number(booking?.number_of_clients || 1), appointment_date: booking?.appointment_date || '', appointment_time: normalizeTimeValue(booking?.appointment_time || '') });
    initializedRef.current = false;
  }, [booking, clientUser]);

  useEffect(() => {
    if (!booking || initializedRef.current) return;
    setSelectedServices(buildSelectedServices(booking, services));
    initializedRef.current = true;
  }, [booking, services]);

  useEffect(() => {
    if (!booking) return;
    fetch(`${API_BASE}/get_branches.php`).then(r => r.json()).then(d => { if (d.success) setBranches(d.branches || []); }).catch(() => setBranches([]));
  }, [booking]);

  useEffect(() => {
    if (!formData.branch_id) { setTherapists([]); return; }
    fetch(`${API_BASE}/get_therapists.php?branch_id=${formData.branch_id}`).then(r => r.json()).then(d => { if (d.success) setTherapists(d.therapists || []); }).catch(() => setTherapists([]));
  }, [formData.branch_id]);

  const availableServices = useMemo(() => {
    if (!formData.branch_id) return services;
    const branchId = Number(formData.branch_id);
    return services.filter((s) => { const ids = Array.isArray(s.branch_ids) ? s.branch_ids : []; return !ids.length || ids.some(id => Number(id) === branchId); });
  }, [services, formData.branch_id]);

  useEffect(() => {
    if (!formData.branch_id || !services.length) return;
    const allowed = new Set(availableServices.map(s => String(s.id)));
    setSelectedServices(prev => prev.filter(svc => allowed.has(String(svc.id))));
  }, [formData.branch_id, availableServices, services.length]);

  const toggleService = (service) => {
    setSelectedServices(prev => {
      const exists = prev.find(svc => String(svc.id) === String(service.id));
      return exists ? prev.filter(svc => String(svc.id) !== String(service.id)) : [...prev, service];
    });
  };

  const totalAmount = selectedServices.reduce((sum, svc) => sum + Number(svc.price || 0), 0) * Number(formData.number_of_clients || 1);
  const totalDuration = selectedServices.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.contact_number) { alert('Please enter your name and contact number'); return; }
    if (formData.contact_number.length !== 11) { alert('Contact number must be exactly 11 digits.'); return; }
    if (!formData.branch_id) { alert('Please select a branch'); return; }
    if (!formData.appointment_date || !formData.appointment_time) { alert('Please select preferred date and time'); return; }
    if (selectedServices.length === 0) { alert('Please select at least one service'); return; }
    onSave({ ...formData, services: selectedServices });
  };

  // Lock body scroll
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-[2rem] sm:rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{ maxHeight: '95svh' }}>
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="px-5 sm:px-6 pt-3 sm:pt-6 pb-4 flex justify-between items-center border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">Edit Booking</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5">
          {booking.manager_comment && (
            <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm font-bold text-amber-800">Manager's Suggestion:</p>
              <p className="text-sm text-amber-700 mt-1">{booking.manager_comment}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name + Contact — stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name</label>
                <input type="text" value={formData.full_name} onChange={e => setFormData(p => ({ ...p, full_name: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Contact Number</label>
                <input type="tel" value={formData.contact_number} onChange={e => setFormData(p => ({ ...p, contact_number: normalizeContact(e.target.value) }))} inputMode="numeric" pattern="[0-9]{11}" maxLength={11} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm" required />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Branch</label>
              <select value={formData.branch_id} onChange={e => setFormData(p => ({ ...p, branch_id: e.target.value, therapist_id: '' }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm appearance-none" required>
                <option value="">Select a branch</option>
                {branches.map(b => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}{b.address ? ` - ${b.address}` : ''}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Services</label>
              <div className="border border-gray-200 rounded-xl max-h-48 overflow-y-auto">
                {availableServices.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No services available for this branch.</div>
                ) : (
                  availableServices.map(service => {
                    const isSelected = selectedServices.some(svc => String(svc.id) === String(service.id));
                    return (
                      <label key={service.id} className={`flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors ${isSelected ? 'bg-[#faf5ef]' : 'bg-white'}`}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleService(service)} className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-700 truncate">{service.name}</p>
                          {service.duration_minutes ? <p className="text-xs text-gray-500">{service.duration_minutes} mins</p> : null}
                        </div>
                        <div className="text-xs font-bold text-[#6f4a42] flex-shrink-0">₱{Number(service.price || 0).toLocaleString()}</div>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">Selected: {selectedServices.length}</p>
            </div>

            {/* Date + Time + Clients — stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">No. of Clients</label>
                <input type="number" min="1" value={formData.number_of_clients} onChange={e => setFormData(p => ({ ...p, number_of_clients: Math.max(1, Number(e.target.value) || 1) }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm" required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Date</label>
                <input type="date" value={formData.appointment_date} onChange={e => setFormData(p => ({ ...p, appointment_date: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm" required min={new Date().toISOString().split('T')[0]} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Time</label>
                <select value={formData.appointment_time} onChange={e => setFormData(p => ({ ...p, appointment_time: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm appearance-none" required>
                  <option value="">Select a time</option>
                  {['09:00:00','10:00:00','11:00:00','12:00:00','13:00:00','14:00:00','15:00:00','16:00:00','17:00:00','18:00:00','19:00:00','20:00:00'].map((t, i) => {
                    const labels = ['9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM','7:00 PM','8:00 PM'];
                    return <option key={t} value={t}>{labels[i]}</option>;
                  })}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Therapist (Optional)</label>
              <select value={formData.therapist_id} onChange={e => setFormData(p => ({ ...p, therapist_id: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89F80] text-sm appearance-none">
                <option value="">No Preference</option>
                {therapists.map(t => <option key={t.therapist_id} value={t.therapist_id}>{t.name}</option>)}
              </select>
            </div>

            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Total Duration</span>
                <span>{totalDuration > 0 ? `${totalDuration} mins` : '-'}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-gray-800 mt-2">
                <span>Total Amount</span>
                <span>₱{Number(totalAmount || 0).toLocaleString()}</span>
              </div>
            </div>
          </form>
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 px-5 sm:px-6 py-4 border-t border-gray-100 flex gap-3 bg-white">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition">Cancel</button>
          <button type="button" disabled={saving} onClick={() => {
            if (!formData.full_name || !formData.contact_number) { alert('Please enter your name and contact number'); return; }
            if (formData.contact_number.length !== 11) { alert('Contact number must be exactly 11 digits.'); return; }
            if (!formData.branch_id) { alert('Please select a branch'); return; }
            if (!formData.appointment_date || !formData.appointment_time) { alert('Please select preferred date and time'); return; }
            if (selectedServices.length === 0) { alert('Please select at least one service'); return; }
            onSave({ ...formData, services: selectedServices });
          }} className="flex-1 px-4 py-3 bg-[#B89F80] text-white rounded-xl font-bold text-sm hover:bg-[#9a8368] transition disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Receipt Modal ──────────────────────────────────────────────────────────
const ReceiptModal = ({ booking, onClose, clientUser }) => {
  const services = booking.services || [];
  const numClients = booking.number_of_clients || 1;
  const clientName = clientUser?.full_name || booking.guest_name || 'Client';
  const receiptDate = new Date().toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const singleServicePrice = services.length > 0 ? services[0].price : booking.service_price || 0;
  const receiptId = booking.receipt_code || String(booking.booking_id).padStart(7, '0');
  const totalPriceNumber = Number(booking.total_price || (Number(singleServicePrice) * numClients) || 0);
  const amountPaidNumber = Number(booking.amount_paid || 0);
  const amountDueNumber = Number(booking.amount_due ?? Math.max(0, totalPriceNumber - amountPaidNumber));
  const paymentLabel = booking.payment_method === 'qrph' ? 'QR Ph' : (booking.payment_method || 'On-Site Payment');
  const totalPrice = totalPriceNumber.toLocaleString();
  const receiptRef = useRef(null);
  const uploadOnceRef = useRef(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);

  useEffect(() => {
    let isMounted = true;
    if (uploadOnceRef.current || isUploadingReceipt) return () => { isMounted = false; };
    const uploadReceiptImage = async () => {
      if (!receiptRef.current) return;
      uploadOnceRef.current = true;
      setIsUploadingReceipt(true);
      try {
        await new Promise(r => setTimeout(r, 60));
        const canvas = await captureReceiptCanvas(2);
        if (!canvas) throw new Error('Failed to generate receipt image');
        const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
        if (!blob) throw new Error('Failed to generate receipt image');
        const dataUrl = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(blob); });
        await fetch(`${API_BASE}/upload_receipt_image.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ booking_id: booking.booking_id, image_data: dataUrl }) });
      } catch (err) { console.error('Failed to upload receipt image:', err); }
      finally { if (isMounted) setIsUploadingReceipt(false); }
    };
    uploadReceiptImage();
    return () => { isMounted = false; };
  }, [booking.booking_id, isUploadingReceipt]);

  const formatMoney = (value) => Number(value || 0).toLocaleString();
  const formatDate = (dateStr) => { if (!dateStr) return '-'; return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
  const formatTime = (timeStr) => { if (!timeStr) return '-'; const [hour, minute] = timeStr.split(':'); const temp = new Date(); temp.setHours(Number(hour || 0), Number(minute || 0), 0, 0); return temp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
  const formatDuration = (totalMinutes) => { if (!totalMinutes) return '-'; if (totalMinutes < 60) return `${totalMinutes} mins`; const hours = Math.floor(totalMinutes / 60); const mins = totalMinutes % 60; if (mins === 0) return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`; return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${mins} mins`; };
  const totalDurationMinutes = services.length > 0 ? services.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0) : Number(booking.duration_minutes || 0);

  const captureReceiptCanvas = async (scale) => {
    if (!receiptRef.current) return null;
    const el = receiptRef.current;
    const prev = { maxHeight: el.style.maxHeight, height: el.style.height, overflow: el.style.overflow, overflowY: el.style.overflowY };
    const prevScrollTop = el.scrollTop;
    el.style.maxHeight = 'none'; el.style.height = 'auto'; el.style.overflow = 'visible'; el.style.overflowY = 'visible'; el.scrollTop = 0;
    try { await new Promise(r => setTimeout(r, 50)); return await html2canvas(el, { scale, backgroundColor: '#ffffff', useCORS: true }); }
    finally { el.style.maxHeight = prev.maxHeight; el.style.height = prev.height; el.style.overflow = prev.overflow; el.style.overflowY = prev.overflowY; el.scrollTop = prevScrollTop; }
  };

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    try {
      const canvas = await captureReceiptCanvas(1);
      if (!canvas) return;
      const downloadImage = (blob) => { if (!blob) return; const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `Hygea-Receipt-${receiptId}.png`; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url); };
      canvas.toBlob ? canvas.toBlob(downloadImage, 'image/png') : fetch(canvas.toDataURL('image/png')).then(r => r.blob()).then(downloadImage);
    } catch (err) { console.error('Failed to download receipt image:', err); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="w-full sm:max-w-sm bg-[#f6f5f2] text-gray-800 font-mono shadow-2xl border-0 sm:border border-gray-300 rounded-t-[2rem] sm:rounded-lg overflow-hidden flex flex-col" style={{ maxHeight: '95svh' }}>
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0 bg-[#f6f5f2]">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Scrollable receipt content */}
        <div ref={receiptRef} className="flex-1 overflow-y-auto px-6 py-4 bg-[#f6f5f2]">
          <div className="text-center">
            <div className="text-lg font-bold tracking-[0.3em]">HYGEA WELLNESS</div>
            <div className="text-[11px] text-gray-600">Branch: {booking.branch_name || 'Main Branch'}</div>
            <div className="text-[11px] text-gray-600">Address: {booking.branch_address || '-'}</div>
            <div className="text-[11px] text-gray-600">Receipt #{receiptId}</div>
            <div className="text-[11px] text-gray-600">{receiptDate}</div>
          </div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-center text-sm font-bold tracking-[0.3em]">APPOINTMENT RECEIPT</div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-[11px] text-gray-500 space-y-0.5">
            <div>Client: {clientName}</div>
            <div>No. of Person/s: {numClients}</div>
            <div>Appointment: {formatDate(booking.appointment_date)} {formatTime(booking.appointment_time)}</div>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-gray-600">
            <span>Description</span><span>Amount</span>
          </div>
          <div className="my-2 border-t border-dashed border-gray-300" />
          <div className="space-y-3">
            {services.length > 0 ? services.map((svc, idx) => (
              <div key={idx}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{svc.service_name}</span>
                  <span className="font-semibold">₱{formatMoney(Number(svc.price || 0) * numClients)}</span>
                </div>
                <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(svc.duration_minutes || 0))}</div>
              </div>
            )) : (
              <div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{booking.service_name || 'Service'}</span>
                  <span className="font-semibold">₱{formatMoney(Number(singleServicePrice || 0) * numClients)}</span>
                </div>
                <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(booking.duration_minutes || 0))}</div>
              </div>
            )}
          </div>
          <div className="my-4 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Total Duration</span><span>{formatDuration(totalDurationMinutes)}</span>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Amount Paid (50%)</span><span>₱{formatMoney(amountPaidNumber)}</span>
          </div>
          <div className="flex justify-between text-sm font-semibold text-gray-600 mt-2">
            <span>Remaining Balance</span><span>₱{formatMoney(amountDueNumber)}</span>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span><span>₱{totalPrice}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-2">Payment: {paymentLabel}</div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-center text-sm font-bold tracking-[0.4em]">THANK YOU!</div>
          <div className="text-center text-[10px] text-gray-500 mt-4">Hygea Wellness - All Rights Reserved (c) 2024</div>
        </div>

        {/* Sticky action buttons */}
        <div className="flex-shrink-0 flex flex-col gap-2 px-6 py-4 border-t border-gray-200 bg-[#f6f5f2]" data-html2canvas-ignore="true">
          <button onClick={handleDownload} className="w-full py-3 bg-gray-900 hover:bg-black text-white font-bold uppercase text-sm tracking-wide rounded-xl transition-all">Download Receipt</button>
          <button onClick={onClose} className="w-full py-3 bg-[#B89F80] hover:bg-[#9a8368] text-white font-bold uppercase text-sm tracking-wide rounded-xl transition-all">Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────
const ClientPurchased = () => {
  const [clientUser, setClientUser] = useState(() => getStoredClientUser());
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, pending: 0, approved: 0, declined: 0, cancelled: 0, completed: 0, total_spent: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedReceiptBooking, setSelectedReceiptBooking] = useState(null);
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  const userId = clientUser?.user_id;

  useEffect(() => {
    const onStorage = () => setClientUser(getStoredClientUser());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchBookings = async () => {
    if (!userId) { setLoading(false); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get_client_bookings.php?user_id=${userId}`);
      const data = await response.json();
      if (data.success) { setBookings(data.bookings); setStatistics(data.statistics); }
      else setError(data.message || 'Failed to fetch bookings');
    } catch { setError('Failed to connect to server'); }
    finally { setLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const data = await fetch(`${API_BASE}/get_services.php`).then(r => r.json());
      if (data.success) setServices(data.services);
    } catch { }
  };

  useEffect(() => { fetchBookings(); fetchServices(); }, [userId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightParam = params.get('highlight');
    if (!highlightParam) { setHighlightedBookingId(null); return; }
    setHighlightedBookingId(highlightParam);
    const cardTimer = setTimeout(() => { const card = document.getElementById(`purchased-${highlightParam}`); if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 150);
    const clearTimer = setTimeout(() => setHighlightedBookingId(null), 5000);
    return () => { clearTimeout(cardTimer); clearTimeout(clearTimer); };
  }, [location.search, bookings.length]);

  const formatDate = (dateStr) => { if (!dateStr) return '-'; return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
  const normalizeStatus = (status) => { if (!status) return ''; const s = status.trim().toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); };
  const formatMoney = (value) => Number(value || 0).toLocaleString();
  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case 'Approved':  return 'bg-emerald-100 text-emerald-700';
      case 'Pending':   return 'bg-amber-100 text-amber-700';
      case 'Declined':  return 'bg-red-100 text-red-700';
      case 'Cancelled': return 'bg-gray-100 text-gray-700';
      default:          return 'bg-gray-100 text-gray-700';
    }
  };

  const handleEditBooking = async (formData) => {
    if (!editingBooking || !userId) return;
    const selectedServices = Array.isArray(formData?.services) ? formData.services : [];
    const primaryService = selectedServices[0];
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/update_client_booking.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: editingBooking.booking_id, user_id: userId, guest_name: formData.full_name, guest_contact: formData.contact_number, branch_id: formData.branch_id, therapist_id: formData.therapist_id || null, number_of_clients: formData.number_of_clients, appointment_date: formData.appointment_date, appointment_time: formData.appointment_time, service_id: primaryService?.id || editingBooking.service_id, services: selectedServices.map(s => ({ service_id: s.id, name: s.name, duration_minutes: s.duration_minutes, price: s.price })) })
      });
      const data = await response.json();
      if (data.success) { setEditingBooking(null); fetchBookings(); }
      else alert(data.message || 'Failed to update booking');
    } catch { alert('Failed to connect to server'); }
    finally { setSaving(false); }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      const data = await fetch(`${API_BASE}/update_booking_status.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ booking_id: bookingId, status: 'Cancelled', comment: 'Cancelled by client', actor: 'client' }) }).then(r => r.json());
      if (data.success) { setBookings(prev => prev.filter(b => b.booking_id !== bookingId)); fetchBookings(); }
      else alert(data.message || 'Failed to cancel booking');
    } catch { alert('Failed to connect to server'); }
  };

  const visibleBookings = useMemo(() => bookings.filter(b => {
    const s = (b?.status || '').trim().toLowerCase();
    return s !== 'cancelled' && s !== 'completed';
  }), [bookings]);

  return (
    <div className="scroll-smooth bg-[#F5F2ED] text-[#2D2D2D] font-sans min-h-screen">
      {/* Notification bell */}
      <div className="fixed top-4 right-4 sm:right-6 z-50">
        <NotificationBell isScrolled={false} forceBrown={true} />
      </div>

      <ClientSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        clientName={clientUser?.full_name}
        clientEmail={clientUser?.email}
        clientAvatar={clientUser?.avatar_url}
        onLogout={() => { window.location.href = '/'; }}
      />

      <div className="transition-all duration-300">
        <div className="pt-16 sm:pt-20 pb-8 px-4 sm:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Page header */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">My Purchases</h2>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">View and manage your active bookings</p>
            </div>

            {/* Stats — 1 col on mobile, 3 on sm+ */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Active Bookings</p>
                <p className="text-2xl font-bold text-[#6f4a42] mt-1">{statistics.pending + statistics.approved}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-bold text-amber-500 mt-1">{statistics.pending}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Pending Total Cost</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">₱{Number(statistics.pending_cost || 0).toLocaleString()}</p>
              </div>
            </div>

            {/* Bookings List */}
            <div className="space-y-4">
              {loading ? (
                <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200 text-center">
                  <p className="text-gray-500">Loading bookings...</p>
                </div>
              ) : error ? (
                <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200 text-center">
                  <p className="text-red-500">{error}</p>
                  <button onClick={fetchBookings} className="mt-4 px-4 py-2 bg-[#B89F80] text-white rounded-lg font-bold text-sm hover:bg-[#9a8368]">Retry</button>
                </div>
              ) : visibleBookings.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200 text-center">
                  <p className="text-gray-500">No active bookings found</p>
                </div>
              ) : (
                visibleBookings.map((booking, index) => {
                  const bookingKey = booking?.booking_id ?? index;
                  const isHighlighted = highlightedBookingId === String(bookingKey);
                  const normalizedStatus = normalizeStatus(booking.status);
                  const canViewReceipt = ['Pending', 'Approved', 'Completed'].includes(normalizedStatus)
                    && Number(booking.amount_paid || 0) > 0;
                  return (
                    <div
                      key={bookingKey}
                      id={`purchased-${bookingKey}`}
                      className={`overflow-hidden rounded-2xl border shadow-sm transition bg-white ${isHighlighted ? 'border-[#b37b65] shadow-lg ring-2 ring-[#b37b65]/50' : 'border-gray-200'}`}
                    >
                      <div className="p-4 sm:p-6">
                        {/* Card header — stacks on mobile */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-0 mb-4">
                          <div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800">{booking.service_name || 'Service'}</h3>
                            <p className="text-sm text-gray-500">{booking.therapist_name || ''}</p>
                          </div>
                          <div className="sm:text-right">
                            <span className="text-xl sm:text-2xl font-bold text-[#6f4a42]">₱{Number(booking.total_price || 0).toLocaleString()}</span>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Cost</p>
                          </div>
                        </div>

                        {/* Info grid — 2 cols on mobile, 4 on sm+ */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 py-3 sm:py-4 border-t border-b border-gray-100 mb-4 sm:mb-6">
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Branch</p>
                            <p className="text-sm font-semibold text-gray-700 mt-0.5">{booking.branch_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Date</p>
                            <p className="text-sm font-semibold text-gray-700 mt-0.5">{formatDate(booking.appointment_date)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Time</p>
                            <p className="text-sm font-semibold text-gray-700 mt-0.5">{booking.appointment_time || '-'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Status</p>
                            <span className={`inline-block mt-0.5 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${getStatusColor(booking.status)}`}>
                              {normalizedStatus}
                            </span>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Amount Paid</p>
                            <p className="text-sm font-semibold text-emerald-600 mt-0.5">₱{formatMoney(booking.amount_paid || 0)}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">50% down payment</p>
                          </div>
                        </div>

                        {/* Manager suggestion */}
                        {booking.manager_comment && (
                          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-start gap-3">
                              <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-amber-800">Suggestion from Manager</p>
                                <p className="text-sm text-amber-700 mt-1">{booking.manager_comment}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Action buttons — full-width stack on mobile */}
                        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
                          {booking.manager_comment && normalizedStatus !== 'Declined' && (
                            <button onClick={() => setEditingBooking(booking)} className="w-full sm:w-auto px-5 py-2.5 bg-[#B89F80] text-white rounded-xl font-bold text-sm hover:bg-[#9a8368] transition">
                              Edit Booking
                            </button>
                          )}
                          {canViewReceipt && (
                            <button onClick={() => setSelectedReceiptBooking(booking)} className="w-full sm:w-auto px-5 py-2.5 bg-blue-500 text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition shadow-md">
                              View Receipt
                            </button>
                          )}
                          {(normalizedStatus === 'Pending' || normalizedStatus === 'Approved') && (
                            <button onClick={() => handleCancelBooking(booking.booking_id)} className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-red-50 hover:text-red-600 transition">
                              Cancel Booking
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          services={services}
          clientUser={clientUser}
          onClose={() => setEditingBooking(null)}
          onSave={handleEditBooking}
          saving={saving}
        />
      )}

      {selectedReceiptBooking && (
        <ReceiptModal
          booking={selectedReceiptBooking}
          clientUser={clientUser}
          onClose={() => setSelectedReceiptBooking(null)}
        />
      )}

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={(user) => { if ((user?.role || '').toLowerCase() === 'client') setClientUser(user); }}
      />
      <ChatBotTrigger />
    </div>
  );
};

export default ClientPurchased;
